// iter 275 round 34 — BeforeAfterImage component condiviso.
//
// Estratto pure-extract da FotoEditor.jsx e GalleriaFoto.jsx: identico in
// entrambi i lightbox. Tap-and-hold stile iPhone Live Photos per Prima/Dopo
// con pre-caricamento doppia <img> sovrapposta (zero flicker).
//
// Props:
//  - afterSrc:  URL della foto "dopo" (sempre visibile)
//  - beforeSrc: URL della foto "prima" (pre-caricato, opacity 0/100 al press)
//  - isLive:    se true mostra UI prima/dopo (live previews); se false solo after
//  - kindLabel: etichetta tipo foto (per alt attribute e a11y)
//  - lightboxView, setLightboxView: stato esterno "before" | "after"
//  - onClose: callback per chiudere il lightbox (tap area nera)
//  - testidPrefix: prefisso per data-testid ("foto-lightbox" | "gallery-lightbox")

import React from "react";
import { ArrowsHorizontal } from "@phosphor-icons/react";

export function BeforeAfterImage({
  afterSrc,
  beforeSrc,
  isLive,
  kindLabel,
  lightboxView,
  setLightboxView,
  onClose,
  testidPrefix,
}) {
  const hasBefore = isLive && !!beforeSrc;

  return (
    <>
      <div
        className="flex-1 flex items-center justify-center px-3 pb-3 overflow-hidden cursor-pointer relative"
        onClick={onClose}
      >
        {/* iter v30 r098 — Container con altezza esplicita: entrambe le
            immagini (before + after) sono assolute con w-full h-full
            object-contain → vengono scalate ESATTAMENTE allo stesso modo.
            Prima il bug: l'after era in flusso e dettava le dimensioni del
            padre, la before era absolute con object-contain dentro un
            bounding box diverso → su desktop la "Prima" appariva più piccola
            della "Dopo" se l'AI restituiva un'immagine con aspect ratio
            leggermente diverso dall'originale. */}
        <div
          className="relative w-full h-full max-w-full select-none"
          onClick={(e) => e.stopPropagation()}
          onMouseDown={hasBefore ? () => setLightboxView("before") : undefined}
          onMouseUp={hasBefore ? () => setLightboxView("after") : undefined}
          onMouseLeave={hasBefore ? () => setLightboxView("after") : undefined}
          onTouchStart={hasBefore ? (e) => { e.preventDefault(); setLightboxView("before"); } : undefined}
          onTouchEnd={hasBefore ? () => setLightboxView("after") : undefined}
          onTouchCancel={hasBefore ? () => setLightboxView("after") : undefined}
          onContextMenu={(e) => e.preventDefault()}
          // iter 275 round 38: disabilita il menu contestuale nativo iOS
          // ("Salva immagine in…", "Copia immagine", ecc.) che intercettava
          // il tap-and-hold e creava confusione.
          style={{
            touchAction: "manipulation",
            WebkitTouchCallout: "none",
            WebkitUserSelect: "none",
            userSelect: "none",
          }}
        >
          <img
            src={afterSrc}
            alt={kindLabel}
            className="absolute inset-0 w-full h-full object-contain rounded-lg shadow-2xl select-none block"
            draggable={false}
            data-testid={`${testidPrefix}-image`}
            style={{ WebkitTouchCallout: "none", WebkitUserSelect: "none", pointerEvents: "none" }}
          />
          {/* before overlay: pre-caricato sempre, opacity 0→100 al press.
              Stesso bounding box dell'after → object-contain produce
              dimensioni IDENTICHE per entrambe le foto. */}
          {hasBefore && (
            <img
              src={beforeSrc}
              alt="Prima"
              className="absolute inset-0 w-full h-full object-contain rounded-lg select-none block transition-opacity duration-100 ease-out pointer-events-none"
              draggable={false}
              style={{
                opacity: lightboxView === "before" ? 1 : 0,
                WebkitTouchCallout: "none",
                WebkitUserSelect: "none",
              }}
              data-testid={`${testidPrefix}-image-before`}
            />
          )}
          {/* Indicatore stato corrente (top-left della foto) */}
          {hasBefore && (
            <div
              className="absolute top-2 left-2 z-10 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-[0.16em] transition-all duration-150"
              style={{
                background: lightboxView === "before"
                  ? "rgba(255,255,255,0.92)"
                  : "rgba(0,0,0,0.5)",
                color: lightboxView === "before" ? "#1C1C1A" : "#FFFFFF",
                backdropFilter: "blur(8px)",
                WebkitBackdropFilter: "blur(8px)",
              }}
            >
              {lightboxView === "before" ? "Prima" : "Dopo"}
            </div>
          )}
        </div>
      </div>
      {/* Hint testuale stile iOS — chiaro e visibile, ma discreto */}
      {hasBefore && (
        <div
          className="absolute top-[max(env(safe-area-inset-top),58px)] left-1/2 -translate-x-1/2 z-[55] pointer-events-none"
          data-testid={`${testidPrefix}-hint`}
        >
          <div
            className="px-3.5 py-1.5 rounded-full text-white text-[12px] font-semibold tracking-[0.02em] inline-flex items-center gap-2 transition-opacity duration-200 shadow-lg"
            style={{
              opacity: lightboxView === "before" ? 0 : 1,
              background: "rgba(0,0,0,0.55)",
              backdropFilter: "blur(14px) saturate(180%)",
              WebkitBackdropFilter: "blur(14px) saturate(180%)",
              border: "0.5px solid rgba(255,255,255,0.12)",
            }}
          >
            <ArrowsHorizontal size={13} weight="bold" />
            Tieni premuto sulla foto per il prima
          </div>
        </div>
      )}
    </>
  );
}

// iter 275 round 34 — Squircle iOS icon usata sopra le tile (FotoEditor +
// GalleriaFoto). Frosted glass grigio scuro · 18×18 px · radius 5.5 px.
export function PhotoTileIcon({ Icon, size = 12 }) {
  return (
    <div
      className="absolute top-1.5 right-1.5 inline-flex items-center justify-center text-white pointer-events-none"
      style={{
        width: "18px",
        height: "18px",
        borderRadius: "5.5px",
        background: "rgba(38,38,36,0.62)",
        backdropFilter: "blur(10px) saturate(160%)",
        WebkitBackdropFilter: "blur(10px) saturate(160%)",
        boxShadow: "inset 0 0.5px 0 rgba(255,255,255,0.12)",
      }}
    >
      <Icon size={size} weight="bold" />
    </div>
  );
}
